import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AdminCtx = createContext({});
export const useAdmin = () => useContext(AdminCtx);

/**
 * adminFetch — wrapper fetch admin avec credentials:'include' (Sprint Frontend B3)
 *
 * Auth = cookie httpOnly `admin-token` envoyé automatiquement par le navigateur.
 * Plus de header Authorization, plus de localStorage.getItem('admin-token').
 */
export function adminFetch(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
}

const NAV_ITEMS = [
  { path: '/admin/dashboard',          label: 'Tableau de bord',   icon: '▦' },
  { path: '/admin/analytics',          label: 'Analytics',         icon: '📊' },
  { path: '/admin/utilisateurs',       label: 'Utilisateurs',      icon: '👥' },
  { path: '/admin/aides',              label: 'Base aides',        icon: '📋' },
  { path: '/admin/aides/catalogue',    label: 'Catalogue aides',   icon: '🗂️' },
  { path: '/admin/clients',            label: 'Clients agents',    icon: '📞' },
  { path: '/admin/paiements',          label: 'Paiements',         icon: '💰' },
  { path: '/admin/logs',               label: 'Logs',              icon: '📝' },
  { path: '/admin/audit',              label: 'Audit accès',       icon: '🔍' },
];

export default function AdminLayout({ children, title }) {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { status: authStatus, role, logout: authLogout } = useAuth();
  const [dark, setDark] = useState(() => localStorage.getItem('admin-dark') === 'true');
  const [status, setStatus] = useState('checking');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Garde auth : redirige login si non authentifié ou pas admin
  useEffect(() => {
    if (authStatus === 'loading') return;
    if (authStatus !== 'authenticated' || role !== 'admin') {
      navigate('/admin');
    }
  }, [authStatus, role, navigate]);

  // Statut serveur (indépendant de l'auth)
  useEffect(() => {
    fetch('/api/health', { credentials: 'include' })
      .then(r => setStatus(r.ok ? 'online' : 'offline'))
      .catch(() => setStatus('offline'));
  }, []);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('admin-dark', String(next));
  };

  const logout = async () => {
    await authLogout();
    navigate('/admin');
  };

  // Pendant le check auth ou pendant redirect → on n'affiche rien
  if (authStatus === 'loading' || authStatus !== 'authenticated' || role !== 'admin') {
    return null;
  }

  const d = dark;
  const bg      = d ? '#1a1a2e' : '#F5F3F0';
  const sidebar  = d ? '#16213e' : '#FDFCFA';
  const sideText = d ? '#e0e0e0' : '#2D2D2D';
  const border   = d ? '#2d2d4e' : '#D9D9D9';
  const mainBg   = d ? '#0f0f23' : '#F5F3F0';
  const cardBg   = d ? '#1e1e3a' : '#FDFCFA';

  return (
    <AdminCtx.Provider value={{ dark, cardBg, border, sideText }}>
      <div style={{ display: 'flex', minHeight: '100vh', background: bg, fontFamily: 'Inter, sans-serif' }}>

        {/* Overlay mobile */}
        <div
          className={`aidly-admin-overlay${sidebarOpen ? ' overlay-open' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar */}
        <aside
          className={`aidly-admin-sidebar${sidebarOpen ? ' sidebar-open' : ''}`}
          style={{
            width: 260, minHeight: '100vh', background: sidebar,
            borderRight: `1px solid ${border}`, display: 'flex', flexDirection: 'column',
            position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
            boxShadow: '2px 0 8px rgba(0,0,0,0.08)',
          }}
        >
          {/* Logo */}
          <div style={{ padding: '24px 20px', borderBottom: `1px solid ${border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'linear-gradient(135deg, #D4724A, #F0B89A)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 16,
              }}>A</div>
              <div>
                <div style={{ fontFamily: 'Lora, serif', fontWeight: 700, fontSize: 18, color: '#D4724A' }}>Aidly</div>
                <div style={{ fontSize: 11, color: '#8A8A8A', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Admin</div>
              </div>
            </div>
            {/* Statut serveur */}
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: status === 'online' ? '#3D8B5E' : status === 'offline' ? '#C0392B' : '#F0A500',
                boxShadow: status === 'online' ? '0 0 6px rgba(61,139,94,0.5)' : 'none',
              }} />
              <span style={{ color: sideText, opacity: 0.7 }}>
                {status === 'online' ? 'Serveur en ligne' : status === 'offline' ? 'Serveur hors ligne' : 'Vérification…'}
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '12px 16px', borderRadius: 10, marginBottom: 4,
                    background: active ? 'rgba(212,114,74,0.15)' : 'transparent',
                    borderLeft: active ? '3px solid #D4724A' : '3px solid transparent',
                    color: active ? '#D4724A' : sideText,
                    fontSize: 15, fontWeight: active ? 600 : 400,
                    transition: '0.15s ease', cursor: 'pointer',
                  }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = d ? 'rgba(255,255,255,0.05)' : 'rgba(212,114,74,0.07)'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                    {item.label}
                  </div>
                </Link>
              );
            })}
          </nav>

          {/* Bottom actions */}
          <div style={{ padding: '16px 12px', borderTop: `1px solid ${border}` }}>
            <button onClick={toggleDark} style={{
              width: '100%', padding: '10px 16px', borderRadius: 8,
              border: `1px solid ${border}`, background: 'transparent',
              color: sideText, fontSize: 14, cursor: 'pointer', marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              {dark ? '☀️' : '🌙'} {dark ? 'Mode clair' : 'Mode sombre'}
            </button>
            <button onClick={logout} style={{
              width: '100%', padding: '10px 16px', borderRadius: 8,
              border: '1px solid rgba(192,57,43,0.3)', background: 'transparent',
              color: '#C0392B', fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              🚪 Déconnexion
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="aidly-admin-main" style={{ flex: 1, marginLeft: 260, minHeight: '100vh', background: mainBg }}>
          {/* Topbar */}
          <div className="aidly-admin-topbar" style={{
            padding: '20px 32px', borderBottom: `1px solid ${border}`,
            background: cardBg, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            {/* Hamburger mobile */}
            <button
              className="aidly-admin-hamburger"
              onClick={() => setSidebarOpen(o => !o)}
              style={{
                display: 'none',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 22, padding: '4px 8px', color: d ? '#e0e0e0' : '#2D2D2D',
                flexShrink: 0,
              }}
              aria-label="Menu"
            >
              ☰
            </button>
            <h1 style={{
              fontFamily: 'Lora, serif', fontSize: 24, fontWeight: 700,
              color: d ? '#e0e0e0' : '#2D2D2D', margin: 0, flex: 1,
            }}>{title}</h1>
            <div style={{ fontSize: 13, color: '#8A8A8A', flexShrink: 0 }}>
              {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>

          {/* Page content */}
          <div style={{ padding: '32px' }}>
            {children}
          </div>
        </main>
      </div>
    </AdminCtx.Provider>
  );
}

// Composants réutilisables
export function KpiCard({ label, value, sub, color = '#D4724A', icon }) {
  const { dark, cardBg, border } = useAdmin();
  return (
    <div style={{
      background: cardBg, border: `1px solid ${border}`,
      borderRadius: 16, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      borderTop: `3px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 13, color: '#8A8A8A', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: dark ? '#e0e0e0' : '#2D2D2D', fontFamily: 'Lora, serif' }}>{value}</div>
          {sub && <div style={{ fontSize: 13, color: '#8A8A8A', marginTop: 4 }}>{sub}</div>}
        </div>
        {icon && <div style={{ fontSize: 28, opacity: 0.6 }}>{icon}</div>}
      </div>
    </div>
  );
}

export function SectionTitle({ children }) {
  const { dark } = useAdmin();
  return (
    <h2 style={{
      fontFamily: 'Lora, serif', fontSize: 18, fontWeight: 600,
      color: dark ? '#e0e0e0' : '#2D2D2D', margin: '0 0 16px',
    }}>{children}</h2>
  );
}

export function AdminCard({ children, style = {} }) {
  const { cardBg, border } = useAdmin();
  return (
    <div style={{
      background: cardBg, border: `1px solid ${border}`,
      borderRadius: 16, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      ...style,
    }}>
      {children}
    </div>
  );
}

export function Badge({ children, color = '#D4724A', bg }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      color, background: bg || `${color}18`,
      border: `1px solid ${color}44`,
    }}>{children}</span>
  );
}
